import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetCarriers, type Carrier } from "@/api/carriers";

/**
 * Carrier scope — narrows Operations screens to a set of carriers and,
 * optionally, a set of dispatch centers within those carriers.
 *
 * The shape is two parallel arrays:
 *
 *  - `carrierIds`: empty = "nothing selected" (screens render empty payloads).
 *    Default on first load = every SELF-RATED carrier (treat as "all" without
 *    silently widening when new ones are added — the user opts in).
 *  - `dispatchCenterIds`: empty = "no center narrowing" — i.e. show every
 *    center for the selected carriers. Non-empty narrows further.
 *
 * Server endpoints (tasks/trips/operators/dispatch-centers) accept comma-separated
 * `carrierIds=a,b` / `dispatchCenterIds=a,b` query params and short-circuit
 * to an empty payload when the param is sent empty. Consumers join with `,`.
 */
export interface CarrierScopeContextValue {
  carrierIds: string[];
  dispatchCenterIds: string[];
  setCarrierIds: (ids: string[]) => void;
  setDispatchCenterIds: (ids: string[]) => void;
  /**
   * Whether the active screen meaningfully scopes by dispatch center.
   * When false, the picker should render disabled (with a tooltip explanation).
   * Default true — screens opt OUT via {@link useCenterScopeNotApplicable}.
   */
  includeExternalCarriers: boolean;
  setIncludeExternalCarriers: (include: boolean) => void;
  centerScopeApplicable: boolean;
  setCenterScopeApplicable: (v: boolean) => void;
}

const STORAGE_KEY = "admin-web-carrier-scope-v3";
const STORAGE_KEY_V2 = "admin-web-carrier-scope-v2";
const STORAGE_KEY_V1 = "admin-web-carrier-scope-v1";

const CarrierScopeContext = createContext<CarrierScopeContextValue | undefined>(
  undefined
);

interface PersistedScope {
  carrierIds: string[];
  dispatchCenterIds: string[];
}

// Read the persisted v3 scope, migrating from v2 / v1 if necessary.
// `null` = nothing persisted (caller seeds defaults from the OWNED carriers
// list once it loads). Anything malformed collapses to null so a bad write
// never crashes the app.
function readScope(): PersistedScope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return migrateFromV2() ?? migrateFromV1();
    }
    const parsed: unknown = JSON.parse(raw);
    if (isPersistedScope(parsed)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function isPersistedScope(v: unknown): v is PersistedScope {
  return (
    !!v &&
    typeof v === "object" &&
    Array.isArray((v as PersistedScope).carrierIds) &&
    (v as PersistedScope).carrierIds.every((x) => typeof x === "string") &&
    Array.isArray((v as PersistedScope).dispatchCenterIds) &&
    (v as PersistedScope).dispatchCenterIds.every((x) => typeof x === "string")
  );
}

// v2 stored either the string "ALL" or { carrierId, dispatchCenterId } — fold
// the single-carrier case into a one-item array; "ALL" maps to `null` so the
// caller can seed from the OWNED carriers list (the v2 default).
function migrateFromV2(): PersistedScope | null {
  try {
    const v2 = localStorage.getItem(STORAGE_KEY_V2);
    if (v2 === null) return null;

    let upgraded: PersistedScope | null;
    if (v2 === "ALL") {
      upgraded = null; // signal "seed from owned carriers" on first paint
    } else {
      const parsed: unknown = JSON.parse(v2);
      if (
        parsed &&
        typeof parsed === "object" &&
        "carrierId" in parsed &&
        typeof (parsed as { carrierId: unknown }).carrierId === "string"
      ) {
        const carrierId = (parsed as { carrierId: string }).carrierId;
        const dispatchCenterIdRaw = (parsed as { dispatchCenterId?: unknown })
          .dispatchCenterId;
        const dispatchCenterIds =
          typeof dispatchCenterIdRaw === "string" ? [dispatchCenterIdRaw] : [];
        upgraded = { carrierIds: [carrierId], dispatchCenterIds };
      } else {
        upgraded = null;
      }
    }

    if (upgraded !== null) {
      writeScope(upgraded);
    }
    localStorage.removeItem(STORAGE_KEY_V2);
    return upgraded;
  } catch {
    return null;
  }
}

// v1 only stored { carrierId } (or "ALL"). Map identically to v2.
function migrateFromV1(): PersistedScope | null {
  try {
    const v1 = localStorage.getItem(STORAGE_KEY_V1);
    if (v1 === null) return null;

    let upgraded: PersistedScope | null;
    if (v1 === "ALL") {
      upgraded = null;
    } else {
      const parsed: unknown = JSON.parse(v1);
      if (
        parsed &&
        typeof parsed === "object" &&
        "carrierId" in parsed &&
        typeof (parsed as { carrierId: unknown }).carrierId === "string"
      ) {
        upgraded = {
          carrierIds: [(parsed as { carrierId: string }).carrierId],
          dispatchCenterIds: [],
        };
      } else {
        upgraded = null;
      }
    }

    if (upgraded !== null) {
      writeScope(upgraded);
    }
    localStorage.removeItem(STORAGE_KEY_V1);
    return upgraded;
  } catch {
    return null;
  }
}

function writeScope(scope: PersistedScope): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // localStorage may throw in private-mode browsers / quota exceeded —
    // scope still works in-memory, just won't persist.
  }
}

export function CarrierScopeProvider({ children }: { children: ReactNode }) {
  const initial = useRef<PersistedScope | null>(readScope());
  const [carrierIds, setCarrierIdsState] = useState<string[]>(
    initial.current?.carrierIds ?? []
  );
  const [dispatchCenterIds, setDispatchCenterIdsState] = useState<string[]>(
    initial.current?.dispatchCenterIds ?? []
  );
  const [seeded, setSeeded] = useState<boolean>(initial.current !== null);
  const [includeExternalCarriers, setIncludeExternalCarriers] = useState(false);
  const [centerScopeApplicable, setCenterScopeApplicable] =
    useState<boolean>(true);

  // First-load seeding: when nothing was persisted (or v2/v1 was "ALL"), seed
  // `carrierIds` from the list of SELF-RATED carriers once the API resolves.
  // Treat "all self-rated carriers" as the "all" default — explicit + stable,
  // and matches the server semantics (empty = nothing; we never send empty).
  const { data: carriersResp } = useGetCarriers();
  useEffect(() => {
    if (seeded) return;
    const carriers = carriersResp?.data;
    if (!carriers) return;
    const ids = carriers.filter((c) => c.selfRated).map((c) => c.id);
    setCarrierIdsState(ids);
    setSeeded(true);
  }, [seeded, carriersResp]);

  // Persist whenever either array changes, but only after we've seeded so we
  // don't write an empty initial state over a v2 "ALL" migration in progress.
  useEffect(() => {
    if (!seeded) return;
    writeScope({ carrierIds, dispatchCenterIds });
  }, [seeded, carrierIds, dispatchCenterIds]);

  const setCarrierIds = useCallback((ids: string[]) => {
    setCarrierIdsState(ids);
  }, []);

  const setDispatchCenterIds = useCallback((ids: string[]) => {
    setDispatchCenterIdsState(ids);
  }, []);

  const value = useMemo<CarrierScopeContextValue>(
    () => ({
      carrierIds,
      dispatchCenterIds,
      setCarrierIds,
      setDispatchCenterIds,
      includeExternalCarriers,
      setIncludeExternalCarriers,
      centerScopeApplicable,
      setCenterScopeApplicable,
    }),
    [
      carrierIds,
      dispatchCenterIds,
      setCarrierIds,
      setDispatchCenterIds,
      includeExternalCarriers,
      centerScopeApplicable,
    ]
  );

  return (
    <CarrierScopeContext.Provider value={value}>
      {children}
    </CarrierScopeContext.Provider>
  );
}

export function useCarrierScope(): CarrierScopeContextValue {
  const ctx = useContext(CarrierScopeContext);
  if (ctx === undefined) {
    throw new Error(
      "useCarrierScope must be used within a CarrierScopeProvider"
    );
  }
  return ctx;
}

/**
 * Opt the current screen OUT of dispatch-center scoping. Mount in screens that
 * are conceptually carrier-scoped but not center-scoped (e.g. carrier-wide
 * configuration screens, add-on service catalogs). The top-bar center picker
 * renders disabled (with an explanatory tooltip) while this screen is active.
 */
export function useCenterScopeNotApplicable(): void {
  const { setCenterScopeApplicable } = useCarrierScope();
  useEffect(() => {
    setCenterScopeApplicable(false);
    return () => setCenterScopeApplicable(true);
  }, [setCenterScopeApplicable]);
}

// Test-only: re-exported for the test file to keep using the type guard
// without forcing the test to re-implement it.
export const __test_only = { isPersistedScope };

// Convenience: filter SELF-RATED carriers from a list. Exported so the filter
// components (CarrierFilter) can reuse without re-implementing.
export function selfRatedCarriers(carriers: Carrier[]): Carrier[] {
  return carriers.filter((c) => c.selfRated);
}

/** Shipment lists include label providers as well as self-rated operators. */
export function useAllCarrierOptions(): void {
  const { setIncludeExternalCarriers } = useCarrierScope();
  useEffect(() => {
    setIncludeExternalCarriers(true);
    return () => setIncludeExternalCarriers(false);
  }, [setIncludeExternalCarriers]);
}
